import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  CreditCard,
  Check,
  Calendar,
  Clock,
  AlertCircle,
  MapPin,
  Navigation,
  Star,
  Shield,
  Timer,
  X,
  Lock,
  CheckCircle,
  Users,
  Globe,
  Award
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { Header } from '../components/Layout/Header'
import { subscriptionService, TrialStatusResponse } from '../services/subscriptionService'
import AnalyticsService from '../services/analyticsService'
import { SUPPORT_EMAIL } from '../constants/contact'
import emailService from '../services/emailService'
import { useLocalization } from '../contexts/LocalizationContext'
import { useAudit } from '../hooks/useAudit'

const SubscriptionV5 = () => {
  const { user, subscription, checkSubscription } = useAuth()
  const navigate = useNavigate()
  const { testAuditLogging } = useAudit()
  const [loading, setLoading] = useState(true)
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [paymentResult, setPaymentResult] = useState<{
    type: 'success' | 'error'
    title: string
    message: string
    details?: Record<string, string>
  } | null>(null)
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [paymentFormDisplayed, setPaymentFormDisplayed] = useState(false)
  const [trialStatus, setTrialStatus] = useState<TrialStatusResponse | null>(null)
  const [currentTrialSubscriptionId, setCurrentTrialSubscriptionId] = useState<string | null>(null)
  const [currentFormToken, setCurrentFormToken] = useState<string | null>(null)
  const { t, formatPrice, config } = useLocalization()

  // États pour le formulaire de paiement intégré
  const [formData, setFormData] = useState({
    email: user?.email || '',
    fullName: '',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    loadSubscriptionInfo()
    loadSystemPayScript()
  }, [])

  const loadSystemPayScript = () => {
    // Charger le script SystemPay avec la bonne langue
    if (!document.querySelector('script[src*="kr-payment-form"]')) {
      const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'

      const script = document.createElement('script')
      script.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/stable/kr-payment-form.min.js'
      script.setAttribute('kr-public-key', '67361502:publickey_YK1k7LNpmzU6usYlVGXEO4WOMAsezDDdgnongY2ErOFhe')
      script.setAttribute('kr-language', systemPayLanguage)
      document.head.appendChild(script)

      console.log('🌍 SystemPay chargé avec la langue:', systemPayLanguage)

      // Charger le thème Neon
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.href = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon-reset.min.css'
      document.head.appendChild(link)

      const neonScript = document.createElement('script')
      neonScript.src = 'https://static.systempay.fr/static/js/krypton-client/V4.0/ext/neon.js'
      document.head.appendChild(neonScript)
    }
  }

  const loadSubscriptionInfo = async () => {
    try {
      setLoading(true)
      await checkSubscription()
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'abonnement:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    if (!formData.email || !formData.email.includes('@')) {
      errors.email = 'Adresse e-mail invalide'
    }

    if (!formData.fullName || formData.fullName.length < 6 || formData.fullName.length > 25) {
      errors.fullName = 'Le nom doit contenir entre 6 et 25 caractères'
    }

    if (!formData.cardNumber || formData.cardNumber.replace(/\s/g, '').length < 13) {
      errors.cardNumber = 'Numéro de carte invalide'
    }

    if (!formData.expiryMonth) {
      errors.expiryMonth = 'Mois requis'
    }

    if (!formData.expiryYear) {
      errors.expiryYear = 'Année requise'
    }

    if (!formData.cvv || formData.cvv.length < 3) {
      errors.cvv = 'CVV invalide'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleSubscribe = async () => {
    if (!user?.email) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Début de la création d\'abonnement')

      const result = await subscriptionService.createSubscriptionWithPayment(user.email)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)

        // Afficher le formulaire de paiement SystemPay
        await displayPaymentForm(result.data.formToken)
        setShowPaymentForm(true)

        // Démarrer la surveillance du paiement
        startPaymentPolling(result.data.orderId)

      } else {
        throw new Error(result.message || 'Erreur lors de la création du paiement')
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: error instanceof Error ? error.message : t('errors.generic')
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const handleIntegratedPayment = async () => {
    if (!validateForm()) return

    try {
      setPaymentLoading(true)
      setPaymentResult(null)

      console.log('🚀 Début du paiement intégré V5')

      // Pour V5, on utilise la même logique que V3 mais avec le formulaire intégré
      const result = await subscriptionService.createTrialSubscriptionV3(user?.email || formData.email, user?.id)

      if (result.success && result.data) {
        setCurrentOrderId(result.data.orderId)
        setCurrentTrialSubscriptionId(result.data.subscriptionId)
        setCurrentFormToken(result.data.formToken)

        // Afficher le formulaire SystemPay intégré
        setShowPaymentForm(true)
        await displayPaymentForm(result.data.formToken)

        // Démarrer la surveillance du paiement
        startTrialPaymentPolling(result.data.orderId)

      } else {
        throw new Error(result.message || 'Erreur lors de la création de l\'essai')
      }

    } catch (error) {
      console.error('❌ Erreur:', error)
      setPaymentResult({
        type: 'error',
        title: t('errors.generic'),
        message: error instanceof Error ? error.message : t('errors.generic')
      })
    } finally {
      setPaymentLoading(false)
    }
  }

  const displayPaymentForm = async (formToken: string) => {
    try {
      // Attendre que le script SystemPay soit chargé
      await new Promise<void>((resolve) => {
        const checkKR = () => {
          if (window.KR) {
            resolve()
          } else {
            setTimeout(checkKR, 100)
          }
        }
        checkKR()
      })

      if (window.KR) {
        const systemPayLanguage = config?.language === 'fr' ? 'fr-FR' : 'en-GB'
        console.log('🌍 Configuration du formulaire SystemPay avec la langue:', systemPayLanguage)

        await window.KR.setFormConfig({
          formToken: formToken,
          'kr-language': systemPayLanguage
        })

        const container = document.getElementById('payment-container')
        if (container) {
          container.innerHTML = '<div class="kr-smart-form"></div>'
          await window.KR.renderElements('#payment-container')

          // Marquer que le formulaire est affiché
          setPaymentFormDisplayed(true)
        }
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'affichage du formulaire:', error)
      throw error
    }
  }

  const startPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'success',
              title: t('subscriptionV3.paymentSuccessTitle'),
              message: t('subscriptionV3.paymentSuccessMessage'),
              details: {
                [t('subscriptionV3.price')]: formatPrice(result.data.amount / 100),
                [t('landing.pricing.planName')]: t('landing.pricing.planName'),
                [t('common.monthly')]: t('common.monthly')
              }
            })

            // Recharger les informations d'abonnement
            await loadSubscriptionInfo()
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)

          } else if (status === 'failed') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentFailedTitle'),
              message: t('subscriptionV3.paymentFailedMessage')
            })

          } else if (status === 'cancelled') {
            clearInterval(pollInterval)
            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentCancelledTitle'),
              message: t('subscriptionV3.paymentCancelledMessage')
            })
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors de la vérification du statut:', error)
      }
    }, 2000)

    // Arrêter le polling après 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const startTrialPaymentPolling = (orderId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const result = await subscriptionService.getTransactionStatus(orderId)

        if (result.success && result.data) {
          const status = result.data.status

          if (status === 'completed') {
            clearInterval(pollInterval)

            // Tracker le succès de l'abonnement d'essai V5
            AnalyticsService.trackSubscription('trial_v5')
            AnalyticsService.trackEvent('purchase', {
              event_category: 'subscription',
              event_label: 'trial_v5_success',
              currency: config?.currency || 'USD',
              value: 0, // FREE
            })

            // Envoyer l'email de bienvenue d'essai de manière asynchrone
            if (user?.email) {
              console.log('📧 Envoi de l\'email de bienvenue d\'essai pour:', user.email)
              emailService.sendTrialWelcomeEmailAsync(user.email, user.email.split('@')[0])
                .catch(error => {
                  console.warn('⚠️ Erreur lors de l\'envoi de l\'email de bienvenue (non bloquant):', error)
                })
            }

            setPaymentResult({
              type: 'success',
              title: t('subscription.trial.activated'),
              message: t('subscription.trial.message'),
              details: {
                [t('subscription.trial.duration')]: t('subscription.trial.duration'),
                [t('subscription.trial.cost')]: t('subscription.trial.cost'),
                [t('subscription.trial.after')]: formatPrice() + t('common.perMonth')
              }
            })

            // Reload subscription info
            await loadSubscriptionInfo()
            setShowPaymentForm(false)

            // Rediriger vers le dashboard après 2 secondes
            setTimeout(() => {
              navigate('/dashboard')
            }, 2000)

          } else if (status === 'failed') {
            clearInterval(pollInterval)

            // Tracker l'échec du paiement
            AnalyticsService.trackError('payment_failed', 'Trial V5 payment method registration failed')

            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationFailedTitle'),
              message: t('subscriptionV3.paymentRegistrationFailedMessage')
            })

          } else if (status === 'cancelled') {
            clearInterval(pollInterval)

            // Tracker l'annulation du paiement
            AnalyticsService.trackEvent('payment_cancelled', {
              event_category: 'subscription',
              event_label: 'trial_v5_payment_cancelled',
            })

            setPaymentResult({
              type: 'error',
              title: t('subscriptionV3.paymentRegistrationCancelledTitle'),
              message: t('subscriptionV3.paymentRegistrationCancelledMessage')
            })
          }
        }
      } catch (error) {
        console.error('❌ Error checking payment status:', error)
      }
    }, 2000)

    // Stop polling after 5 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 300000)
  }

  const handleCancelSubscription = async () => {
    if (!subscription || !user?.email) return

    const subscriptionType = subscription.status === 'trial' ? 'trial' : 'subscription'
    const confirmMessage = subscription.status === 'trial'
      ? t('subscriptionV3.confirmCancelTrial')
      : t('subscriptionV3.confirmCancelSubscription')

    if (!window.confirm(confirmMessage)) {
      return
    }

    try {
      setCancelLoading(true)
      console.log('🚫 Début de l\'annulation pour:', user.email)

      // Utiliser la nouvelle méthode qui récupère automatiquement l'ID
      const result = await subscriptionService.cancelSubscription(user.email, true)

      if (result.success) {
        // Tracker l'annulation d'abonnement V5
        AnalyticsService.trackEvent('subscription_cancelled', {
          event_category: 'subscription',
          event_label: subscription.status === 'trial' ? 'trial_v5_cancelled' : 'subscription_v5_cancelled',
        })

        // Envoyer l'email d'annulation de manière asynchrone
        if (user?.email) {
          console.log('📧 Envoi de l\'email d\'annulation pour:', user.email, 'Type:', subscriptionType)
          emailService.sendCancellationEmailAsync(
            user.email,
            subscriptionType as 'trial' | 'subscription',
            user.email.split('@')[0],
            subscription.expiryDate
          ).catch(error => {
            console.warn('⚠️ Erreur lors de l\'envoi de l\'email d\'annulation (non bloquant):', error)
          })
        }

        const successMessage = subscription.status === 'trial'
          ? t('subscriptionV3.cancelSuccessTrial')
          : t('subscriptionV3.cancelSuccessSubscription')

        alert(successMessage)

        // Recharger les informations d'abonnement
        await loadSubscriptionInfo()

        // Rediriger vers la page d'accueil après 2 secondes
        setTimeout(() => {
          navigate('/')
        }, 2000)

      } else {
        throw new Error(result.message || t('errors.generic'))
      }

    } catch (error) {
      console.error('❌ Erreur lors de l\'annulation:', error)

      const errorMessage = error instanceof Error ? error.message : t('errors.generic')
      alert(`${t('errors.generic')}: ${errorMessage}`)

      // Si l'erreur indique qu'aucun abonnement n'a été trouvé, recharger les infos
      if (errorMessage.includes('Aucun abonnement trouvé') || errorMessage.includes('No subscription found')) {
        console.log('🔄 Rechargement des informations d\'abonnement après erreur')
        await loadSubscriptionInfo()
      }

    } finally {
      setCancelLoading(false)
    }
  }

  const getStatusInfo = () => {
    if (!subscription) return { status: 'none', message: '', color: 'gray' }

    if (subscription.status === 'active') {
      return {
        status: 'active',
        message: 'Active',
        color: 'green'
      }
    }

    if (subscription.status === 'trial') {
      return {
        status: 'trial',
        message: t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext }),
        color: 'blue'
      }
    }

    if (subscription.status === 'cancelled') {
      return {
        status: 'cancelled',
        message: `${t('common.cancelled')} - ${t('subscriptionV3.accessRemains')} ${subscription.expiryDate}`,
        color: 'orange'
      }
    }

    return {
      status: subscription.status,
      message: subscription.status,
      color: 'gray'
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <Header />

      {/* Main content */}
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header accrocheur inspiré de la page externe */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Shield className="w-8 h-8 text-green-600 mr-3" />
              <h1 className="text-3xl font-bold text-gray-900">
                {t('subscriptionV5.unlimitedAccess')}
              </h1>
            </div>

            {/* Badges de sécurité */}
            <div className="flex items-center justify-center space-x-6 mb-6">
              <div className="flex items-center text-sm text-gray-600">
                <Lock className="w-4 h-4 mr-2 text-green-600" />
                <span>{t('subscriptionV5.sslEncrypted')}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 mr-2 text-green-600" />
                <span>{t('subscriptionV5.tls256')}</span>
              </div>
            </div>

            {/* Prix mis en avant */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mb-8 inline-block">
              <div className="text-center">
                <div className="text-5xl font-bold text-green-600 mb-2">
                  0{t('common.currency')}
                </div>
                <div className="text-lg text-gray-600 mb-2">
                  {t('subscriptionV5.trialActivation')}
                </div>
                <div className="text-sm text-gray-500">
                  {t('subscriptionV5.noDebitForActivation')}
                </div>
              </div>
            </div>
          </div>

          {/* Affichage de l'abonnement d'essai actif */}
          {subscription && subscription.status === 'trial' && (
            <div className="bg-white shadow rounded-lg mb-8">
              <div className="p-6">
                <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg p-6">
                  <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Timer className="w-8 h-8 text-blue-600" />
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">
                      {t('subscriptionV3.trialActive')}
                    </h4>
                    <div className="text-center mb-4">
                      <div className="text-lg font-semibold text-blue-600 mb-1">
                        {t('subscriptionV3.remainingDays', { count: subscription.daysUntilNext })}
                      </div>
                      <div className="text-sm text-gray-600">
                        {t('subscriptionV3.trialEndsOn')} {subscription.trialEndDate ? new Date(subscription.trialEndDate).toLocaleDateString() : subscription.expiryDate}
                      </div>
                    </div>

                    {/* Trial benefits */}
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                      <div className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5" />
                        <div className="text-left">
                          <h5 className="text-sm font-medium text-green-900 mb-1">
                            {t('subscriptionV3.fullAccessTitle')}
                          </h5>
                          <p className="text-sm text-green-700">
                            {t('subscriptionV3.fullAccessDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center">
                      <MapPin className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('landing.features.smartMap.title')}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('landing.features.saveTime.description')}</span>
                    </div>
                    <div className="flex items-center">
                      <Navigation className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('landing.features.navigation.title')}</span>
                    </div>
                    <div className="flex items-center">
                      <Star className="h-5 w-5 text-green-600 mr-3" />
                      <span className="text-gray-700">{t('landing.features.favorites.title')}</span>
                    </div>
                  </div>

                  <div className="text-center">
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelLoading}
                      className="bg-red-600 text-white py-2 px-6 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50 mr-4"
                    >
                      {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelTrial')}
                    </button>
                    <p className="text-xs text-gray-500 mt-2">
                      {t('subscriptionV3.cancelTrialInfo')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pas d'abonnement ou abonnement expiré */}
          {(!subscription || (subscription.status !== 'active' && subscription.status !== 'trial')) && (
            <div className="bg-white shadow rounded-lg mb-8">
              <div className="p-6">

                {/* Section des avantages avec icônes */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <MapPin className="w-6 h-6 text-blue-600" />
                    </div>
                    <span className="text-sm text-gray-600">{t('subscriptionV5.features.interactiveMap')}</span>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Clock className="w-6 h-6 text-green-600" />
                    </div>
                    <span className="text-sm text-gray-600">{t('subscriptionV5.features.realTimeAvailability')}</span>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Navigation className="w-6 h-6 text-purple-600" />
                    </div>
                    <span className="text-sm text-gray-600">{t('subscriptionV5.features.gpsNavigation')}</span>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Shield className="w-6 h-6 text-orange-600" />
                    </div>
                    <span className="text-sm text-gray-600">{t('subscriptionV5.features.noUpfrontCharges')}</span>
                  </div>
                </div>

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t('subscriptionV5.unlimitedHighSpeedAccess')}
                  </h3>
                  <p className="text-gray-600">
                    {t('subscriptionV5.accessDescription')}
                  </p>
                </div>

                {!showPaymentForm ? (
                  <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6">
                    <div className="text-center mb-6">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Timer className="w-8 h-8 text-green-600" />
                      </div>
                      <div className="text-center mb-6">
                        <div className="text-3xl font-bold text-green-600 mb-1">
                          {t('subscriptionV5.startNow')}
                        </div>
                        <div className="text-sm text-gray-600 bg-green-100 inline-block px-3 py-1 rounded-full">
                          ✨ {t('subscriptionV5.completelyFree')}
                        </div>
                      </div>
                    </div>

                    <div className="text-center">
                      <button
                        onClick={() => setShowPaymentForm(true)}
                        disabled={paymentLoading}
                        className="bg-green-600 text-white py-3 px-8 rounded-lg text-lg font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                      >
                        {paymentLoading ? t('subscriptionV3.processing') : t('subscriptionV5.continue')}
                      </button>
                      <p className="text-xs text-gray-500 mt-2">
                        {t('subscriptionV5.completelyFree')}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4 text-center">
                      <p className="text-gray-700 text-sm font-bold">
                        {t('subscriptionV5.paymentMethodRegistration')}
                      </p>
                    </div>

                    <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div>
                        <p className="text-sm text-green-700">{t('subscriptionV5.paymentMethodDescription')}</p>
                        <p className="text-sm text-green-800 font-medium mt-2">
                          {t('subscriptionV5.noChargeInfo')}
                        </p>
                      </div>
                    </div>

                    {/* Formulaire de paiement intégré inspiré de la page externe */}
                    <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                      <div className="space-y-4">
                        {/* Email */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            E-mail
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleFormChange('email', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              formErrors.email ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Entrez une adresse e-mail valide"
                          />
                          {formErrors.email && (
                            <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>
                          )}
                        </div>

                        {/* Nom complet */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('subscriptionV5.fullName')}
                          </label>
                          <input
                            type="text"
                            value={formData.fullName}
                            onChange={(e) => handleFormChange('fullName', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              formErrors.fullName ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Le nom doit contenir entre 6 et 25 caractères"
                          />
                          {formErrors.fullName && (
                            <p className="text-red-500 text-xs mt-1">{formErrors.fullName}</p>
                          )}
                        </div>

                        {/* Numéro de carte */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('subscriptionV5.cardNumber')}
                          </label>
                          <input
                            type="text"
                            value={formData.cardNumber}
                            onChange={(e) => handleFormChange('cardNumber', e.target.value)}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                              formErrors.cardNumber ? 'border-red-500' : 'border-gray-300'
                            }`}
                            placeholder="Numéro de carte de crédit invalide"
                            maxLength={19}
                          />
                          {formErrors.cardNumber && (
                            <p className="text-red-500 text-xs mt-1">{formErrors.cardNumber}</p>
                          )}
                        </div>

                        {/* Date d'expiration et CVV */}
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('subscriptionV5.expiryMonth')}
                            </label>
                            <select
                              value={formData.expiryMonth}
                              onChange={(e) => handleFormChange('expiryMonth', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                formErrors.expiryMonth ? 'border-red-500' : 'border-gray-300'
                              }`}
                            >
                              <option value="">MM</option>
                              {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                <option key={month} value={month.toString().padStart(2, '0')}>
                                  {month.toString().padStart(2, '0')}
                                </option>
                              ))}
                            </select>
                            {formErrors.expiryMonth && (
                              <p className="text-red-500 text-xs mt-1">{formErrors.expiryMonth}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('subscriptionV5.expiryYear')}
                            </label>
                            <select
                              value={formData.expiryYear}
                              onChange={(e) => handleFormChange('expiryYear', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                formErrors.expiryYear ? 'border-red-500' : 'border-gray-300'
                              }`}
                            >
                              <option value="">YYYY</option>
                              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i).map(year => (
                                <option key={year} value={year.toString()}>
                                  {year}
                                </option>
                              ))}
                            </select>
                            {formErrors.expiryYear && (
                              <p className="text-red-500 text-xs mt-1">{formErrors.expiryYear}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {t('subscriptionV5.securityCode')}
                            </label>
                            <input
                              type="text"
                              value={formData.cvv}
                              onChange={(e) => handleFormChange('cvv', e.target.value)}
                              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                formErrors.cvv ? 'border-red-500' : 'border-gray-300'
                              }`}
                              placeholder="CVV"
                              maxLength={4}
                            />
                            {formErrors.cvv && (
                              <p className="text-red-500 text-xs mt-1">{formErrors.cvv}</p>
                            )}
                          </div>
                        </div>

                        {/* Conditions d'acceptation */}
                        <div className="text-xs text-gray-600">
                          <p>
                            {t('subscriptionV5.termsAcceptance')}{' '}
                            <Link to="/terms" className="text-blue-600 hover:text-blue-800">
                              {t('subscriptionV5.termsAndConditions')}
                            </Link>
                            ,{' '}
                            <Link to="/privacy" className="text-blue-600 hover:text-blue-800">
                              {t('subscriptionV5.privacyPolicy')}
                            </Link>{' '}
                            {t('subscriptionV5.andFeesBelow')}
                          </p>
                        </div>

                        {/* Bouton continuer */}
                        <button
                          onClick={handleIntegratedPayment}
                          disabled={paymentLoading}
                          className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors duration-200 disabled:opacity-50"
                        >
                          {paymentLoading ? t('subscriptionV3.processing') : t('subscriptionV5.continue')}
                        </button>
                      </div>
                    </div>

                    {paymentLoading && (
                      <div className="text-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">{t('subscriptionV3.loadingPaymentForm')}</p>
                      </div>
                    )}

                    <div id="payment-container" className="min-h-[400px]">
                      {/* Le formulaire SystemPay sera injecté ici */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Résultat du paiement */}
          {paymentResult && (
            <div className={`mb-8 p-4 rounded-lg ${
              paymentResult.type === 'success'
                ? 'bg-green-50 border border-green-200'
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-start">
                {paymentResult.type === 'success' ? (
                  <Check className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5" />
                )}
                <div>
                  <h4 className={`text-sm font-medium ${
                    paymentResult.type === 'success' ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {paymentResult.title}
                  </h4>
                  <p className={`text-sm mt-1 ${
                    paymentResult.type === 'success' ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {paymentResult.message}
                  </p>
                  {paymentResult.details && (
                    <div className="mt-2 text-sm">
                      {Object.entries(paymentResult.details).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="font-medium">{key}:</span>
                          <span>{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Éléments de confiance inspirés de la page externe */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 text-center">
                {t('subscriptionV5.whyChooseUs')}
              </h3>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    {t('subscriptionV5.trust.experts')}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('subscriptionV5.trust.expertsDesc')}
                  </p>
                </div>

                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
                    <Shield className="w-6 h-6 text-green-600" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    {t('subscriptionV5.trust.secure')}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('subscriptionV5.trust.secureDesc')}
                  </p>
                </div>

                <div className="flex flex-col items-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-3">
                    <Globe className="w-6 h-6 text-purple-600" />
                  </div>
                  <h4 className="text-sm font-medium text-gray-900 mb-1">
                    {t('subscriptionV5.trust.alwaysOnline')}
                  </h4>
                  <p className="text-xs text-gray-600">
                    {t('subscriptionV5.trust.alwaysOnlineDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Message promotionnel inspiré de la page externe */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-6 mb-8">
            <div className="text-center">
              <h3 className="text-lg font-bold text-gray-900 mb-3">
                {t('subscriptionV5.trialOffer.title')}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed">
                {t('subscriptionV5.trialOffer.description', {
                  price: formatPrice(),
                  duration: '24 heures'
                })}
              </p>
              <div className="mt-4 text-xs text-gray-600">
                <p>{t('subscriptionV5.trialOffer.note')}</p>
              </div>
            </div>
          </div>

          {/* Abonnement actif */}
          {subscription && subscription.status === 'active' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('subscriptionV3.activeSubscription.title')}
                </h3>
              </div>
              <div className="p-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <Check className="h-5 w-5 text-green-400 mr-3 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-medium text-green-900">
                        {t('subscriptionV3.activeSubscription.statusTitle')}
                      </h4>
                      <p className="text-sm text-green-700 mt-1">
                        {t('subscriptionV3.activeSubscription.statusDescription')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">{t('subscriptionV3.activeSubscription.planDetails')}</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>{t('subscriptionV3.activeSubscription.plan')}:</span>
                        <span className="font-medium">{t('landing.pricing.planName')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('subscriptionV3.activeSubscription.price')}:</span>
                        <span className="font-medium">{formatPrice(subscription.price)}{t('common.perMonth')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('subscriptionV3.activeSubscription.status')}:</span>
                        <span className="font-medium text-green-600">{t('subscriptionV3.activeSubscription.active')}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2">{t('subscriptionV3.activeSubscription.nextBilling')}</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <div className="flex justify-between">
                        <span>{t('subscriptionV3.activeSubscription.nextPayment')}:</span>
                        <span className="font-medium">
                          {subscription.nextBilling ? new Date(subscription.nextBilling).toLocaleDateString() : 'N/A'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('subscriptionV3.activeSubscription.daysUntilBilling')}:</span>
                        <span className="font-medium">{subscription.daysUntilNext || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="bg-red-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors duration-200 disabled:opacity-50"
                  >
                    {cancelLoading ? t('subscriptionV3.cancelling') : t('subscriptionV3.cancelSubscription')}
                  </button>
                  <p className="text-xs text-gray-500 mt-2">
                    {t('subscriptionV3.activeSubscription.cancelInfo')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default SubscriptionV5
